import { useCallback, useEffect, useSyncExternalStore } from "react";

import {
  DEFAULT_THEME,
  THEME_STORAGE_KEY,
  applyTheme,
  getStoredTheme,
  isTheme,
  resolveInitialTheme,
} from "@/lib/theme";

/**
 * A tiny module-level store keeps every toggle in sync — the header and the
 * mobile menu render a ThemeToggle at the same time, and same-tab `storage`
 * events don't fire, so a plain hook-local state would let them diverge.
 *
 * `useSyncExternalStore` also makes hydration correct: React uses
 * `getServerSnapshot` while hydrating, then re-reads `getSnapshot`, so a
 * returning lagoon user never trips a hydration mismatch.
 */
let currentTheme = DEFAULT_THEME;
let initialized = false;
const listeners = new Set();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return currentTheme;
}

function getServerSnapshot() {
  return DEFAULT_THEME;
}

function setCurrent(next, { persist }) {
  if (!isTheme(next) || next === currentTheme) {
    // Still apply/persist so callers can force a re-apply if needed.
    applyTheme(next, { persist });
    return;
  }
  currentTheme = next;
  applyTheme(next, { persist });
  emit();
}

/**
 * Runs once per app session: reconciles the store with the theme the no-flash
 * script already put on <html>, then follows cross-tab changes.
 *
 * The theme is light by default; nothing follows the OS `prefers-color-scheme`,
 * so a first-time visitor always gets the light theme until they flip the
 * toggle themselves.
 */
function initTheme() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  currentTheme = resolveInitialTheme();
  applyTheme(currentTheme, { persist: false });

  window.addEventListener("storage", (event) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    const stored = getStoredTheme();
    if (stored) setCurrent(stored, { persist: false });
  });
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * The reveal is a circle centred on the control the user pressed, grown until
 * it covers the farthest viewport corner — that radius is why we take the max
 * of each axis rather than the diagonal of the viewport.
 */
function resolveRevealCircle(origin) {
  if (!origin || typeof window === "undefined") return null;
  const halfWidth = Math.max(origin.x, window.innerWidth - origin.x);
  const halfHeight = Math.max(origin.y, window.innerHeight - origin.y);
  return { x: origin.x, y: origin.y, radius: Math.hypot(halfWidth, halfHeight) };
}

function revealThemeChange(apply, origin) {
  const doc = document;

  if (prefersReducedMotion()) {
    apply();
    return;
  }

  const circle = resolveRevealCircle(origin);

  if (typeof doc.startViewTransition !== "function" || !circle) {
    // Fallback for browsers without the View Transitions API: a short, cheap
    // cross-fade of the color properties only.
    const root = doc.documentElement;
    root.classList.add("theme-transition");
    // Force a style flush so the transition property is live before the colors
    // change; otherwise there is nothing to animate from.
    void root.offsetWidth;
    apply();
    window.setTimeout(() => root.classList.remove("theme-transition"), 320);
    return;
  }

  const root = doc.documentElement;
  root.style.setProperty("--vt-x", `${circle.x}px`);
  root.style.setProperty("--vt-y", `${circle.y}px`);
  root.style.setProperty("--vt-r", `${circle.radius}px`);
  root.classList.add("disable-element-transitions");

  const transition = doc.startViewTransition(apply);
  transition.finished.finally(() => {
    root.classList.remove("disable-element-transitions");
    root.style.removeProperty("--vt-x");
    root.style.removeProperty("--vt-y");
    root.style.removeProperty("--vt-r");
  });
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    initTheme();
  }, []);

  const setTheme = useCallback((next, origin) => {
    if (!isTheme(next)) return;
    revealThemeChange(() => setCurrent(next, { persist: true }), origin);
  }, []);

  const toggleTheme = useCallback((origin) => {
    const next = currentTheme === "lagoon" ? "light" : "lagoon";
    revealThemeChange(() => setCurrent(next, { persist: true }), origin);
  }, []);

  return { theme, setTheme, toggleTheme };
}
